import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SearchInput from "@/components/SearchInput/SearchInput";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { createAttemptForCompany } from "../../api/createAttemptForCompany";
import { listDiagnostics } from "../../api/listDiagnostics";
import { listSelectableCompanies } from "../../api/listSelectableCompanies";
import { isCancellation } from "../../utils/rejections";
import type { LoadStatus } from "../../utils/loadStatus";
import type {
  DiagnosticSummary,
  SelectableCompany,
} from "../../types/diagnostic";

/**
 * Bounded so the picker never pulls the whole tenant table into a select.
 *
 * The limit is not a page: the endpoint has no cursor, so `q` is the only way
 * to reach a company that falls outside it.
 */
const COMPANY_LIMIT = 50;

/** The directory's, so the two searches feel the same under the fingers. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Rows the picker can actually act on.
 *
 * Radix `SelectItem` **throws** on an empty-string value, so a single row
 * arriving without an id used to take down the whole dialog — the screen a
 * coach opens to start work, and a thrown render is the worst failure mode
 * available here. A non-numeric id has nothing to send to
 * `attempts-for-company/{id}` either, so such a row is dropped rather than
 * listed: offering it would only move the failure later and further from its
 * cause.
 *
 * The endpoint guarantees an integer id (work item 10, B-1) — this is the belt
 * to that brace, not a loosening of the type.
 */
const isSelectable = (company: SelectableCompany): boolean =>
  Number.isFinite(company.companyId);

interface NewDiagnosticDialogProps {
  /** Pre-selects the company and hides the picker, for the per-company page. */
  companyId?: number;
  onClose: () => void;
  open: boolean;
}

/**
 * Start a diagnostic for a company. The subject is the business, so the only
 * choices are which tenant and which instrument.
 *
 * Errors are left to the global interceptor — there is no local error state
 * and no local error extractor.
 */
export const NewDiagnosticDialog: React.FC<NewDiagnosticDialogProps> = ({
  companyId,
  onClose,
  open,
}) => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<SelectableCompany[]>([]);
  const [diagnostics, setDiagnostics] = useState<DiagnosticSummary[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const debouncedCompanySearch = useDebouncedValue(
    companySearch,
    SEARCH_DEBOUNCE_MS,
  );
  /**
   * The term the request actually goes out with.
   *
   * An empty box is honoured at once rather than 300ms later. The debounce
   * exists to coalesce keystrokes, and clearing the field is not a keystroke
   * to coalesce — while the reset below *is* synchronous, so a dialog that had
   * been narrowed to "acme" reopened with an empty search box above a list of
   * Acmes, and the request that fetched them carried "acme" too.
   */
  const searchTerm =
    companySearch.trim() === "" ? "" : debouncedCompanySearch.trim();
  const [slug, setSlug] = useState("");
  // Loading before either request has been made, because "not fetched yet" is
  // what these mean to the markup below. Starting them settled painted a frame
  // of "No companies to choose from." between the dialog opening and its
  // effects running — a small version of the mistake these states exist to
  // stop: an empty array is not on its own an answer.
  const [companiesStatus, setCompaniesStatus] = useState<LoadStatus>("loading");
  const [diagnosticsStatus, setDiagnosticsStatus] =
    useState<LoadStatus>("loading");
  const loadingCompanies = companiesStatus === "loading";
  const loadingDiagnostics = diagnosticsStatus === "loading";
  const [submitting, setSubmitting] = useState(false);

  const companyLocked = companyId !== undefined;

  // Reset per change of opening, not per fetch: the search below re-runs the
  // company request, and it must not wipe what has already been chosen each
  // time it does.
  //
  // Closing resets too. The dialog itself stays mounted — only its content
  // unmounts — so its state outlives an opening, and leaving the previous
  // search in place until the *next* opening meant the reopening reset raced
  // the fetch effect it was supposed to precede.
  //
  // The *answers* are reset with the inputs, not only the inputs. `companies`,
  // `diagnostics` and `truncated` are overwritten by a successful reply and by
  // nothing else, so an opening used to begin on the previous one's list under
  // a "Loading…" trigger, with a truncation hint describing rows nobody had
  // asked for yet.
  useEffect(() => {
    setSelectedCompany(companyId !== undefined ? String(companyId) : "");
    setCompanySearch("");
    setSlug("");
    setCompanies([]);
    setDiagnostics([]);
    setTruncated(false);
  }, [open, companyId]);

  useEffect(() => {
    if (!open) return;
    setDiagnosticsStatus("loading");

    const controller = new AbortController();
    const { signal } = controller;

    listDiagnostics(signal)
      .then((allDiagnostics) => {
        // Only a published instrument can be administered.
        const published = allDiagnostics.filter(
          (item) => item.publishedVersion !== null,
        );
        setDiagnostics(published);
        // With a single instrument there is nothing to choose between, so pick
        // it and drop the field. The select comes back if a second is
        // published rather than needing to be re-added.
        if (published.length === 1) {
          setSlug(published[0].slug);
        }
        setDiagnosticsStatus("loaded");
      })
      .catch((error) => {
        // A cancelled request does not own the status — reopening the dialog
        // aborts the previous load, which settles after the new one has
        // already set it, and a supersession is not a failure.
        //
        // Everything else the interceptor has already toasted. Recording it is
        // what keeps the dialog from reporting a broken request as a platform
        // with nothing published to run (D10).
        if (isCancellation(error)) return;
        // The list goes with the status. Left standing it would be offered for
        // selection under a message saying it could not be read.
        setDiagnostics([]);
        setDiagnosticsStatus("failed");
      });

    return () => controller.abort();
  }, [open]);

  // The company list is fetched separately from the instruments so that typing
  // re-runs only the list the typing narrows.
  //
  // The search reaches the *server*. `limit` is a cap, not a page — the
  // endpoint has no cursor — so filtering the fifty rows already in hand would
  // leave every company past the fiftieth permanently unreachable. The
  // truncation hint used to point at the company's own page, reached from the
  // diagnostics directory, which lists only companies that have *already* been
  // diagnosed: the company a first diagnostic is being started for is by
  // definition not there.
  useEffect(() => {
    if (!open || companyLocked) return;
    setCompaniesStatus("loading");

    const controller = new AbortController();
    const { signal } = controller;

    listSelectableCompanies(
      { limit: COMPANY_LIMIT, q: searchTerm || undefined },
      signal,
    )
      .then((result) => {
        // Filtered on the way into state, so every consumer below — the
        // options, the empty-state hint, the selection check — reads the same
        // list the coach can act on.
        const selectable = result.companies.filter(isSelectable);
        setCompanies(selectable);
        setTruncated(result.hasMore);
        // A narrowed list is a different list. A Radix select whose value
        // names no item it lists falls back to the placeholder, so a selection
        // the search has filtered away would leave the trigger reading "Select
        // a company" with the button still armed — one press from starting a
        // diagnostic for a company the screen was no longer naming.
        setSelectedCompany((current) =>
          current !== "" &&
          !selectable.some((company) => String(company.companyId) === current)
            ? ""
            : current,
        );
        setCompaniesStatus("loaded");
      })
      .catch((error) => {
        // As above: a supersession is not a failure, and a failure must not be
        // left to read as a tenant table with nothing in it.
        if (isCancellation(error)) return;
        // Everything the last successful reply put on screen goes with it. The
        // rows and the hint describe a list that is no longer being claimed,
        // and the selection was taken *from* those rows — leaving it armed is
        // the same defect the narrowed-list branch above guards against: a
        // trigger showing its placeholder over a button one press from
        // starting a diagnostic for a company the screen no longer names.
        setCompanies([]);
        setTruncated(false);
        setSelectedCompany("");
        setCompaniesStatus("failed");
      });

    return () => controller.abort();
  }, [open, companyLocked, searchTerm]);

  const handleCreate = () => {
    if (!selectedCompany || !slug) return;
    setSubmitting(true);
    createAttemptForCompany(slug, Number(selectedCompany))
      .then((attempt) => navigate(`/admin/diagnostics/attempts/${attempt.id}`))
      // The interceptor has already told the user; the dialog has nothing to
      // add and builds no error state of its own. Catching only stops the
      // rejection surfacing as an unhandled promise rejection — without it a
      // lost draft race left the console noisy and nothing on screen.
      .catch(() => undefined)
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog onOpenChange={(isOpen) => !isOpen && onClose()} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New diagnostic</DialogTitle>
          <DialogDescription>
            A diagnostic assesses a company&apos;s operations. Pick the
            business, then fill it out.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!companyLocked && (
            <div>
              <Label htmlFor="diagnostic-company-select">Company</Label>
              <SearchInput
                className="mb-2 mt-1"
                onChange={setCompanySearch}
                placeholder="Search companies…"
                value={companySearch}
              />
              <Select
                disabled={loadingCompanies}
                onValueChange={setSelectedCompany}
                value={selectedCompany}
              >
                <SelectTrigger id="diagnostic-company-select">
                  <SelectValue
                    placeholder={
                      loadingCompanies ? "Loading…" : "Select a company"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem
                      key={company.companyId}
                      value={String(company.companyId)}
                    >
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {companiesStatus === "failed" && (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  The company list could not be loaded.
                </p>
              )}
              {companiesStatus === "loaded" && companies.length === 0 && (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  {searchTerm
                    ? "No company matches that search."
                    : "No companies to choose from."}
                </p>
              )}
              {truncated && (
                // Keyed on the term the reply answered, not the one being
                // typed, so the hint always describes the list under it.
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  {searchTerm
                    ? `Showing the first ${COMPANY_LIMIT} matches. Narrow the search to see the rest.`
                    : `Showing the first ${COMPANY_LIMIT} companies. Search by name to reach one that is not listed.`}
                </p>
              )}
            </div>
          )}

          {diagnostics.length > 1 ? (
            <div>
              <Label htmlFor="diagnostic-instrument-select">Diagnostic</Label>
              <Select
                disabled={loadingDiagnostics}
                onValueChange={setSlug}
                value={slug}
              >
                <SelectTrigger id="diagnostic-instrument-select">
                  <SelectValue
                    placeholder={
                      loadingDiagnostics ? "Loading…" : "Select a diagnostic"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {diagnostics.map((diagnostic) => (
                    <SelectItem key={diagnostic.slug} value={diagnostic.slug}>
                      {diagnostic.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            diagnostics.length === 1 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Running{" "}
                <span className="text-gray-900 dark:text-white">
                  {diagnostics[0].name}
                </span>
                .
              </p>
            )
          )}

          {/* A failed load is not a platform with nothing published. The two
              read identically off an empty array, and only one of them is a
              reason for the coach to give up. */}
          {diagnosticsStatus === "failed" && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              The available diagnostics could not be loaded.
            </p>
          )}
          {diagnosticsStatus === "loaded" && diagnostics.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No published diagnostic is available to run.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={!selectedCompany || !slug || submitting}
            onClick={handleCreate}
          >
            {submitting ? "Starting…" : "Start diagnostic"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewDiagnosticDialog;
